import React, { useEffect, useState } from "react";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";
import { Wallet, ArrowDownToLine, IndianRupee, Loader2, X, CheckCircle, XCircle } from "lucide-react";

export default function AdminWallet() {
  const [tab, setTab] = useState("designers");
  return (
    <div className="space-y-4" data-testid="admin-wallet">
      <h2 className="text-2xl font-bold text-[#1a3c5e] inline-flex items-center gap-2"><Wallet className="w-6 h-6 text-[#F7941D]" /> Designer Wallets</h2>
      <div className="flex gap-1 border-b border-gray-200">
        <button onClick={() => setTab("designers")} data-testid="aw-tab-designers" className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === "designers" ? "text-[#1a3c5e] border-[#F7941D]" : "text-gray-500 border-transparent"}`}>Designer Balances</button>
        <button onClick={() => setTab("payouts")} data-testid="aw-tab-payouts" className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === "payouts" ? "text-[#1a3c5e] border-[#F7941D]" : "text-gray-500 border-transparent"}`}>Payout Requests</button>
      </div>
      {tab === "designers" && <Designers />}
      {tab === "payouts" && <Payouts />}
    </div>
  );
}

function Designers() {
  const [list, setList] = useState([]);
  const [openDes, setOpenDes] = useState(null);
  const [ledger, setLedger] = useState(null);

  useEffect(() => { api.get("/admin/wallet/designers").then(r => setList(r.data)); }, []);
  const openLedger = (d) => {
    setOpenDes(d);
    api.get(`/admin/wallet/${d.id}/ledger`).then(r => setLedger(r.data));
  };

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FEF3E2] text-[#1a3c5e] text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Designer</th>
              <th className="text-right px-4 py-3">Earned</th>
              <th className="text-right px-4 py-3">Paid Out</th>
              <th className="text-right px-4 py-3">Pending</th>
              <th className="text-right px-4 py-3">Available</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-gray-500">No designers yet.</td></tr>}
            {list.map(d => (
              <tr key={d.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`aw-d-${d.id}`}>
                <td className="px-4 py-3"><div className="font-semibold">{d.name}</div><div className="text-xs text-gray-500">{d.email}</div></td>
                <td className="px-4 py-3 text-right">{inr(d.earned)}</td>
                <td className="px-4 py-3 text-right">{inr(d.paid_out)}</td>
                <td className="px-4 py-3 text-right text-amber-700">{inr(d.pending_payout)}</td>
                <td className="px-4 py-3 text-right font-bold text-emerald-700">{inr(d.available)}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => openLedger(d)} data-testid={`aw-d-view-${d.id}`} className="text-[#F7941D] hover:underline text-xs">View ledger →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {openDes && ledger && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <div><h3 className="font-bold text-[#1a3c5e]">{ledger.designer?.name}</h3><div className="text-xs text-gray-500">Available: {inr(ledger.balance.available)} · Earned: {inr(ledger.balance.earned)}</div></div>
              <button onClick={() => { setOpenDes(null); setLedger(null); }}><X className="w-5 h-5" /></button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase"><tr><th className="text-left px-4 py-2">Type</th><th className="text-left px-4 py-2">Description</th><th className="text-right px-4 py-2">Amount</th><th className="text-left px-4 py-2">Date</th></tr></thead>
              <tbody>
                {ledger.ledger.map(e => (
                  <tr key={e.id} className="border-t border-gray-100">
                    <td className="px-4 py-2"><span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${e.type === "credit" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{e.type}</span></td>
                    <td className="px-4 py-2 text-xs">{e.description}</td>
                    <td className={`px-4 py-2 text-right font-semibold ${e.type === "credit" ? "text-emerald-700" : "text-rose-700"}`}>{e.type === "credit" ? "+" : "-"} {inr(e.amount)}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">{(e.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function Payouts() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("pending");

  const load = () => api.get(`/admin/wallet/payouts${filter === "all" ? "" : `?status=${filter}`}`).then(r => setList(r.data || []));
  useEffect(() => { load(); }, [filter]);

  const decide = async (id, decision) => {
    const ref = decision === "mark_paid" ? window.prompt("Payment reference (UTR/UPI ID)?") : null;
    if (decision === "mark_paid" && !ref) return;
    const notes = decision === "reject" ? window.prompt("Reason?") || "" : "";
    try {
      await api.post(`/admin/wallet/payouts/${id}/decision`, { decision, reference: ref, notes });
      toast.success("Done");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {["pending", "approved", "paid", "rejected", "all"].map(s => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`aw-payout-filter-${s}`} className={`px-3 py-1 rounded-full text-xs font-bold border ${filter === s ? "bg-[#1a3c5e] text-white border-[#1a3c5e]" : "bg-white text-gray-600 border-gray-300"}`}>{s}</button>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FEF3E2] text-[#1a3c5e] text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Designer</th>
              <th className="text-left px-4 py-3">Method</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-gray-500">No payout requests.</td></tr>}
            {list.map(p => (
              <tr key={p.id} className="border-t border-gray-100" data-testid={`aw-payout-${p.id}`}>
                <td className="px-4 py-3"><div className="font-semibold">{p.designer?.name}</div><div className="text-xs text-gray-500">{p.designer?.email}</div></td>
                <td className="px-4 py-3 text-xs">{p.method} {p.upi_id && `· ${p.upi_id}`}</td>
                <td className="px-4 py-3 text-right font-bold">{inr(p.amount)}</td>
                <td className="px-4 py-3"><span className="text-[10px] uppercase font-bold">{p.status}</span></td>
                <td className="px-4 py-3 text-xs">{(p.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {p.status === "pending" && <>
                      <button data-testid={`aw-payout-approve-${p.id}`} onClick={() => decide(p.id, "approve")} className="p-1.5 hover:bg-emerald-50 rounded" title="Approve"><CheckCircle className="w-4 h-4 text-emerald-600" /></button>
                      <button data-testid={`aw-payout-reject-${p.id}`} onClick={() => decide(p.id, "reject")} className="p-1.5 hover:bg-rose-50 rounded" title="Reject"><XCircle className="w-4 h-4 text-rose-600" /></button>
                    </>}
                    {(p.status === "approved" || p.status === "pending") && (
                      <button data-testid={`aw-payout-paid-${p.id}`} onClick={() => decide(p.id, "mark_paid")} className="p-1.5 hover:bg-blue-50 rounded" title="Mark Paid"><IndianRupee className="w-4 h-4 text-blue-600" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
